import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { MonthService } from '../../../services/month.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { DrawerService } from '../../../services/drawer.service';
import { BucketService, toBucketUpdateDraft } from '../../../services/bucket.service';
import { NgForm, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Bucket } from '../../../types';
import { DRAWER_DATA } from 'src/app/shared/injector-tokens';
import { BucketDrawerData } from 'src/app/services/drawer.service';
import { CurrencyPipe } from '@angular/common';
import { DrawerLayoutComponent, DrawerLayoutState } from '../../drawer-layout/drawer-layout.component';
import { DrawerStatComponent } from '../../drawer-layout/drawer-stat.component';
import { BucketProgressComponent } from '../../../shared/bucket-progress/bucket-progress.component';
import { UiButtonDirective } from '../../../ui/button/button.directive';
import { BalanceAmountComponent } from '../../../shared/balance-amount/balance-amount.component';
import { UiIconComponent } from '../../../ui/icon/icon.component';
import { UiFieldComponent } from '../../../ui/field/field.component';
import { UiInputDirective } from '../../../ui/input/input.directive';
import { AutofocusDirective } from '../../../directives/autofocus.directive';
import { BucketComponent } from '../../../buckets/bucket/bucket.component';
import { GroupService } from '../../../services/group.service';
import { PAGE_SIZE, TransactionService } from '../../../services/transaction.service';
import { TransactionPage } from '../../../types';

function isBucketDrawerData(data: unknown): data is BucketDrawerData {
  return !!data && typeof data === 'object' && 'bucketId' in data;
}

interface RenameBucketFormValue {
  newBucketName?: string;
}

interface BucketContentView {
  bucket: Bucket;
  key: string;
}

@Component({
  selector: 'app-bucket-drawer-wrapper',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './bucket-drawer-wrapper.component.html',
  host: {
    class: 'flex h-full min-h-0 flex-1 flex-col',
  },
  imports: [
    DrawerLayoutComponent,
    DrawerStatComponent,
    BucketProgressComponent,
    UiButtonDirective,
    BalanceAmountComponent,
    UiIconComponent,
    ReactiveFormsModule,
    FormsModule,
    UiFieldComponent,
    UiInputDirective,
    AutofocusDirective,
    BucketComponent,
  ],
  providers: [CurrencyPipe],
})
export class BucketDrawerWrapperComponent {
  private data = inject<BucketDrawerData>(DRAWER_DATA);
  bucketService = inject(BucketService);
  private groupService = inject(GroupService);
  private transactionService = inject(TransactionService);
  private month = inject(MonthService);
  drawerService = inject(DrawerService);
  private readonly currency = inject(CurrencyPipe);

  editName = false;
  private activeBucketId?: string;
  readonly drawerData = computed(() => {
    const data = this.drawerService.currentData();
    return isBucketDrawerData(data) ? data : this.data;
  });
  readonly initialTab = computed(() => this.drawerData().tab);
  readonly bucketContentKey = computed(() => `${this.drawerData().bucketId}:${this.initialTab() ?? 'default'}`);
  private readonly currentMonthYear = toSignal(this.month.currentMonthYear$, {
    initialValue: this.month.monthYearFromDate(),
  });
  readonly categoryPageQuery = injectQuery(() =>
    this.groupService.groupPageQueryOptions(this.currentMonthYear()),
  );
  readonly bucketTransactionsQuery = injectQuery(() =>
    this.transactionService.bucketTransactionsQueryOptions(this.drawerData().bucketId, this.currentMonthYear()),
  );
  readonly transactionsLoading = computed(() => {
    const data = this.bucketTransactionsQuery.data;
    return this.bucketTransactionsQuery.isPending() && !(data ? data() : null);
  });
  readonly bucket = computed(() => {
    const bucket = this.categoryPageQuery
      .data()
      ?.data.flatMap(category => category.buckets)
      .find(categoryBucket => categoryBucket.id === this.drawerData().bucketId);
    if (!bucket) return null;

    return {
      ...bucket,
      transactions: this.bucketTransactionsQuery.data() ?? bucket.transactions ?? this.emptyTransactionPage(bucket),
    };
  });
  // A settled page without the budget (deleted, or a stale link for this month) is an error, not a load.
  readonly layoutState = computed<DrawerLayoutState>(() => {
    if (this.bucket()) return 'ready';
    return this.categoryPageQuery.isPending() ? 'loading' : 'error';
  });
  readonly layoutErrorMessage = computed(() =>
    this.categoryPageQuery.isError()
      ? "We couldn't load this budget. Check your connection and try again."
      : "We couldn't find this budget. It may have been removed, or it isn't in this month.",
  );
  readonly bucketContentViews = computed<BucketContentView[]>(() => {
    const bucket = this.bucket();
    return bucket ? [{ bucket, key: this.bucketContentKey() }] : [];
  });
  private readonly updateBucketMutation = injectMutation(() => this.bucketService.updateBucketMutationOptions());

  constructor() {
    effect(() => {
      const bucketId = this.drawerData().bucketId;
      if (this.activeBucketId !== undefined && this.activeBucketId !== bucketId) this.editName = false;
      this.activeBucketId = bucketId;
    });
  }

  bucketSpent(bucket: Bucket): number {
    return bucket.type === 'INCOME' ? bucket.total : bucket.total * -1;
  }

  headerSubtitle(bucket: Bucket): string | undefined {
    if (bucket.type === 'TRANSFERS') return "Money moved between accounts · doesn't count toward spending";
    if (!bucket.isSetup) return undefined;
    const spent = this.currency.transform(this.bucketSpent(bucket), 'USD', 'symbol', '1.2-2');
    const amount = this.currency.transform(bucket.amount, 'USD', 'symbol', '1.0-2');
    return `${spent} of ${amount}`;
  }

  renameBucket(form: NgForm, bucket: Bucket) {
    const newBucketName = form.controls['newBucketName']?.value as RenameBucketFormValue['newBucketName'];
    if (!newBucketName) return;

    bucket.name = newBucketName;
    this.updateBucketMutation.mutate(
      toBucketUpdateDraft(bucket, {
        name: newBucketName,
      }),
    );
    this.editName = false;
  }

  private emptyTransactionPage(bucket: Bucket): TransactionPage {
    return {
      data: [],
      pageNumber: 1,
      pageSize: PAGE_SIZE,
      totalRecords: 0,
      params: {
        bucketIds: [bucket.id],
        monthYear: bucket.monthYear,
        pageNumber: 1,
        pageSize: PAGE_SIZE,
        suggestedBucketIds: true,
      },
      _objectType: 'transactionPage',
    };
  }
}
